import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowForward, Business, HomeWork, Lock } from '@mui/icons-material';
import api from '../services/api';
import LoginModal from '../modals/LoginModal';
import { isSafeInternalReturnPath } from '../components/SharedLinkGate';

export default function SharedPropertyPreview() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [initialTab, setInitialTab] = useState(0);

  const returnTo = isSafeInternalReturnPath(location.pathname) ? location.pathname : '/investor';

  useEffect(() => {
    let cancelled = false;

    async function fetchPreview() {
      try {
        setLoading(true);
        const propertyResponse = await api.properties.getById(id);
        if (cancelled) return;
        const propertyData = propertyResponse.data;
        setProperty(propertyData);

        if (propertyData?.vendorId) {
          try {
            const vendorResponse = await api.vendors.getById(propertyData.vendorId);
            if (!cancelled) setVendor(vendorResponse.data);
          } catch (vendorError) {
            if (!cancelled) setVendor(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setProperty(null);
          setVendor(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const openAuth = (tab) => {
    setInitialTab(tab);
    setLoginOpen(true);
  };

  const handleAuthenticated = () => {
    navigate(returnTo, { replace: true });
  };

  const signupContext = {
    sourceType: 'property_link',
    propertyId: id,
    vendorId: property?.vendorId || vendor?.uid || vendor?.id || null,
  };

  const marketLabel = [property?.city, property?.state].filter(Boolean).join(', ');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#008080' }} />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar sx={{ width: 88, height: 88, bgcolor: '#008080' }}>
                  <HomeWork fontSize="large" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#008080' }}>
                    Private Off-Market Opportunity
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {property?.propertyType || 'Property details'}{marketLabel ? ` in ${marketLabel}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shared through OffMarkets by {vendor?.name || 'a verified vendor'}.
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ p: 2.5, border: '1px solid #e6eeee', borderRadius: 1, bgcolor: 'rgba(0, 128, 128, 0.04)' }}>
                <Stack spacing={1.5}>
                  <Chip
                    icon={<Lock />}
                    label="Details gated"
                    sx={{ alignSelf: 'flex-start', bgcolor: '#008080', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Sign in to view the full opportunity.
                  </Typography>
                  <Typography color="text.secondary">
                    Create a free investor account to see the address, pricing, photos, disclosures, and vendor contact options.
                  </Typography>
                </Stack>
              </Box>

              {vendor && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar src={vendor.profilePhoto || vendor.profileImage} sx={{ bgcolor: '#008080' }}>
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{vendor.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {[vendor.title, vendor.company].filter(Boolean).join(' at ') || 'Verified real estate professional'}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => openAuth(1)}
                  sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#005252' } }}
                >
                  Create free investor account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuth(0)}
                  sx={{ borderColor: '#008080', color: '#008080' }}
                >
                  Sign in
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Container>

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        defaultUserType="investor"
        initialTab={initialTab}
        lockUserType
        signupContext={signupContext}
        onAuthenticated={handleAuthenticated}
      />
    </Box>
  );
}
